/**
 * Temporary screen-scoped design tokens for **Templates** (`/templates`).
 *
 * Mirrors `Templates.dc.html` 1:1 — every colour, size, radius, spacing and
 * type value the screen needs lives here so the components never inline a
 * literal. Once every screen is re-skinned these get promoted into `src/theme`
 * and this file is deleted (see UI_IMPLEMENTATION_RULES.md § Temporary Screen
 * Tokens).
 */

import { button, contentCard, formField, inkfree, signer, white } from "theme/colors";

export const templatesTokens = {
  /** Content column around the whole screen. */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    paddingBottom: "48px",
  },

  /** Home ▸ Folder ▸ … trail, drawn above the title. */
  breadcrumb: {
    gap: "8px",
    // Same gap the search / filter row below it leaves — keep in step with
    // `toolbar.marginBottom` so the two blocks are spaced alike.
    marginBottom: "14px",
    fontSize: "14px",
    fontWeight: 600,
    iconSize: 16,
    activeColor: signer.ink,
    linkColor: signer.orange.main,
    separatorColor: signer.border.strong,
    separatorSize: 14,
    /** Longer folder names are clipped before the trail wraps. */
    maxLabelChars: 24,
  },

  /** "Templates" title block + the view toggle / Create actions. */
  header: {
    gap: "14px",
    marginBottom: "22px",
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "5px",
  },

  /** Grid / list segmented control. */
  viewToggle: {
    bg: white,
    border: formField.borderWarm,
    radius: "10px",
    padding: "3px",
    buttonWidth: 34,
    buttonHeight: 32,
    buttonRadius: "8px",
    iconSize: 17,
    color: signer.text.meta,
    activeColor: signer.orange.main,
    activeBg: signer.orange.tint,
  },

  /** Filled "Create" split button. */
  primaryButton: {
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    radius: "10px",
    padding: "12px 18px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
    /** Caret half of the split button. */
    caretWidth: 40,
    caretBg: inkfree.dark,
    caretHoverBg: inkfree.darkest,
    caretDivider: "rgba(255,255,255,.25)",
    caretIconSize: 14,
  },

  /** Outlined button used by the empty / fresh state. */
  secondaryButton: {
    bg: white,
    color: signer.text.secondary,
    border: button.secondaryBorderWarm,
    radius: "10px",
    padding: "0 20px",
    height: 46,
    fontSize: "13.5px",
    fontWeight: 700,
    hoverBorder: signer.ink,
    hoverColor: signer.ink,
  },

  /** Search field + the type pills beside it. */
  toolbar: {
    gap: "14px",
    // Kept in step with `breadcrumb.marginBottom` — the trail sits the same
    // distance above this row as this row sits above the grid.
    marginBottom: "14px",
    bg: white,
    border: formField.borderWarm,
    radius: "11px",
    padding: "12px 16px",
    focusBorder: signer.orange.main,
    searchGap: "11px",
    searchIconSize: 18,
    searchIconColor: signer.text.faint,
    inputFontSize: "14px",
    inputFontWeight: 400,
    inputColor: signer.ink,
    placeholderColor: signer.text.faint,
  },

  /** Four-up card grid. */
  grid: {
    columns: 4,
    gap: "20px",
  },

  /** One folder / template card in grid view. */
  card: {
    bg: contentCard.bg,
    border: contentCard.border,
    radius: "16px",
    shadow: contentCard.shadow,
    padding: "20px 20px 18px",
    hoverBorder: "#D9D3E6",
    hoverShadow: "0 6px 20px rgba(33,21,71,.09)",
    headMarginBottom: "16px",
    iconTileSize: 46,
    iconTileRadius: "12px",
    iconSize: 22,
    nameFontSize: "14.5px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    subFontSize: "12.5px",
    subColor: signer.text.subtitle,
    subMarginTop: "5px",
    createdFontSize: "12px",
    createdColor: button.disabledColor,
    createdMarginTop: "3px",
    kebabSize: 30,
    kebabRadius: "8px",
    kebabIconSize: 17,
  },

  /** White card wrapping the list view. */
  list: {
    bg: white,
    border: contentCard.border,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    gridTemplateColumns: "minmax(0,1fr) 150px 190px 190px 44px",
    gap: "18px",
    headPadding: "13px 24px",
    headFontSize: "11.5px",
    headFontWeight: 700,
    headLetterSpacing: ".06em",
    headColor: signer.text.faint,
    divider: signer.border.divider,
  },

  /** One folder / template row in list view. */
  row: {
    padding: "14px 24px",
    hoverBg: signer.surface.hover,
    leadGap: "13px",
    iconTileSize: 40,
    iconTileRadius: "10px",
    folderIconSize: 19,
    docIconSize: 18,
    nameFontSize: "14px",
    nameFontWeight: 700,
    nameColor: signer.ink,
    subFontSize: "12px",
    subColor: signer.text.faint,
    subMarginTop: "2px",
    typeFontSize: "12.5px",
    typeFontWeight: 600,
    typeColor: signer.text.meta,
    createdFontSize: "12.5px",
    createdColor: signer.text.subtitle,
  },

  /** Row / card overflow menu trigger. */
  kebab: {
    size: 32,
    radius: "8px",
    color: signer.text.meta,
    iconSize: 18,
    hoverBg: signer.border.divider,
    hoverColor: signer.ink,
  },

  /** "Nothing matches …" block shown in place of the grid / list. */
  empty: {
    bg: white,
    border: contentCard.border,
    radius: "14px",
    padding: "64px 24px",
    titleFontSize: "14px",
    titleFontWeight: 600,
    titleColor: signer.text.meta,
    subFontSize: "12.5px",
    subMarginTop: "4px",
    subColor: signer.text.faint,
  },

  /** Infinite-scroll footer under the grid / list. */
  footer: {
    gap: "10px",
    marginTop: "24px",
    spinnerSize: 18,
    /**
     * The design draws a 2.5px ring on an 18px circle; MUI expresses stroke
     * width against its 44-unit viewBox, so 2.5 / 18 * 44 ≈ 6.1.
     */
    spinnerThickness: 6.1,
    spinnerColor: signer.orange.main,
    loadingFontSize: "13px",
    loadingFontWeight: 600,
    loadingColor: signer.text.subtitle,
    atEndFontSize: "12.5px",
    atEndColor: button.disabledColor,
    shownFontSize: "12.5px",
    shownColor: signer.text.faint,
  },

  /** Full-card placeholder for an account with no templates yet. */
  freshState: {
    bg: white,
    border: contentCard.border,
    radius: "16px",
    shadow: "0 1px 3px rgba(33,21,71,.05)",
    padding: "110px 24px",
    iconSize: 72,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    iconGlyphSize: 30,
    iconMarginBottom: "20px",
    titleFontSize: "18px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyFontSize: "13.5px",
    bodyColor: signer.text.subtitle,
    bodyMaxWidth: 440,
    bodyLineHeight: 1.65,
    bodyMarginTop: "8px",
    actionsMarginTop: "26px",
    actionsGap: "12px",
    buttonHeight: 46,
    buttonPadding: "0 22px",
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Templates - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 differently — the narrow column runs the
 * long subtitle across two lines and pushes "Create" out to a floating pill,
 * while the standard column shortens the subtitle and parks a square Create
 * button beside it. MUI has no breakpoint between the two, so the split is
 * expressed as a raw query and applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Templates - Mobile & Tablet.dc.html`.
 *
 * Only the values that actually differ from the desktop tokens above live
 * here; anything omitted is inherited, so the desktop screen is untouched.
 * `sm` / `std` inside `phone` are the 375 and 390 columns of the reference.
 */
export const templatesResponsiveTokens = {
  phone: {
    /** Content column: 14px gutters at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "14px", std: "16px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** Vertical rhythm between subtitle, search, chips and the list. */
    gap: { sm: "13px", std: "14px" },

    /** The reference's app-bar title scale, reused for the in-page title. */
    titleFontSize: { sm: "16px", std: "16.5px" },
    subtitleFontSize: { sm: "12.5px", std: "13px" },
    subtitleLineHeight: 1.45,
    /** The narrow column keeps the full sentence; 390 uses the short one. */
    subtitleShort: "Reusable documents and folders.",

    /**
     * Full-width "Create" button under the header. Same metrics as Sign
     * Requests' phone button, so the two screens read as one control.
     */
    createButton: {
      padding: "13px 14px",
      radius: "11px",
      fontSize: "14px",
      gap: "9px",
      iconSize: 16,
    },

    /** Trail collapses to a single scrolling line of text crumbs. */
    breadcrumb: {
      gap: "6px",
      marginBottom: "0px",
      fontSize: { sm: "12.5px", std: "13px" },
      separator: "/",
      separatorColor: signer.border.strong,
    },

    search: {
      radius: "11px",
      padding: "11px 13px",
      gap: "9px",
      iconSize: 16,
      inputFontSize: { sm: "13px", std: "13.5px" },
    },

    /** Every item is a single compact row, whatever the view toggle says. */
    list: { gap: "10px" },
    card: {
      radius: "14px",
      padding: { sm: "13px 14px", std: "14px 15px" },
      gap: { sm: "12px", std: "13px" },
      iconTileSize: { sm: 40, std: 42 },
      iconTileRadius: "11px",
      folderIconSize: { sm: 19, std: 20 },
      docIconSize: { sm: 18, std: 19 },
      nameFontSize: { sm: "13.5px", std: "14px" },
      subFontSize: { sm: "11.5px", std: "12px" },
      subMarginTop: "2px",
      kebabSize: { sm: 32, std: 34 },
      kebabRadius: { sm: "8px", std: "9px" },
      kebabIconSize: { sm: 17, std: 18 },
    },

    empty: {
      radius: "14px",
      padding: { sm: "44px 18px", std: "48px 20px" },
      titleFontSize: { sm: "14px", std: "14.5px" },
      titleFontWeight: 700,
      subFontSize: { sm: "12px", std: "12.5px" },
    },

    footer: {
      gap: "9px",
      marginTop: "6px",
      spinnerSize: { sm: 16, std: 17 },
      loadingFontSize: { sm: "12.5px", std: "13px" },
      shownFontSize: { sm: "12px", std: "12.5px" },
    },

    freshState: {
      radius: "16px",
      padding: { sm: "44px 20px", std: "52px 22px" },
      iconSize: { sm: 62, std: 66 },
      iconGlyphSize: { sm: 26, std: 28 },
      iconMarginBottom: { sm: "16px", std: "17px" },
      titleFontSize: { sm: "16px", std: "17px" },
      bodyFontSize: { sm: "12.5px", std: "13px" },
      bodyLineHeight: 1.6,
      actionsMarginTop: { sm: "22px", std: "24px" },
      actionsGap: "9px",
      buttonHeight: 46,
      buttonRadius: "11px",
    },
  },

  tablet: {
    paddingX: "26px",
    paddingTop: "22px",
    paddingBottom: "40px",
    gap: "18px",

    titleFontSize: "24px",
    subtitleFontSize: "13px",
    headerActionsGap: "10px",

    viewToggleIconSize: 16,
    viewToggleInactiveColor: signer.text.faint,

    breadcrumbFontSize: "13.5px",
    breadcrumbSeparatorSize: 13,

    search: {
      padding: "12px 15px",
      gap: "11px",
      iconSize: 17,
      inputFontSize: "13.5px",
    },

    grid: { columns: 2, gap: "16px" },

    card: {
      padding: "18px 18px 16px",
      headMarginBottom: "14px",
      iconTileSize: 44,
      folderIconSize: 21,
      docIconSize: 20,
      nameFontSize: "14px",
      subFontSize: "12px",
      subMarginTop: "5px",
      createdFontSize: "11.5px",
      createdMarginTop: "3px",
    },

    row: { padding: "14px 20px" },

    freshState: {
      padding: "96px 24px",
      titleFontSize: "18px",
    },
  },
} as const;

/**
 * Per-type icon tile tint. Folders take the violet wash, templates the peach
 * one — exactly as `TINT` in `Templates.dc.html`.
 */
export const templatesTypeTokens = {
  folder: { bg: signer.purple.chipBg, stroke: signer.purple.main },
  template: { bg: signer.orange.hover, stroke: signer.orange.main },
} as const;

export type TemplatesTypeKey = keyof typeof templatesTypeTokens;

export default templatesTokens;
